import { cn } from "cn";
import type { ReactNode } from "react";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAnchorSnapshot } from "@/hooks/use-anchor-snapshot";

import { useFilters } from "../_hooks/use-filters";
import {
  operatorLabel as labelForOperator,
  operatorOptions,
} from "../operators";
import type { FilterField } from "../types";
import { ActiveFilterField } from "./active-filter-field";
import { ActiveFilterOperator } from "./active-filter-operator";
import { ActiveFilterRemove } from "./active-filter-remove";

/**
 * Placeholder shown in a chip's value slot before any value is picked — matches
 * the office orders/tasks chips, which render a bare ellipsis for empty filters.
 */
export const EMPTY_VALUE_LABEL = "...";

type ActiveFilterChipProps = {
  field: FilterField;
  valueLabel: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRemove: () => void;
  children: ReactNode;
  showOperator?: boolean;
  /**
   * How many values are currently selected. Lets the set operators read as
   * their singular form for a one-value pick ("is" rather than "is any of").
   */
  selectedCount?: number;
  /**
   * `dateRange` widgets only — the current value is an explicit two-date range
   * (the custom picker) rather than a named preset, so the chip reads
   * "is between Mar 1 - Mar 7" but "is Today". The widget decides: it is the
   * only thing that knows its own preset list.
   */
  isExplicitRange?: boolean;
};

/**
 * Shared chip shell for every filter widget: field label + (optional) operator
 * + a popover trigger showing the current value + a remove button. Widgets pass
 * the value label and the popover body (their selection command).
 *
 * The operator segment is driven by the field's `operators` list: one operator
 * renders as static text, several open a dropdown that rewrites the field's
 * operator in the URL.
 */
export const ActiveFilterChip = ({
  field,
  valueLabel,
  open,
  onOpenChange,
  onRemove,
  children,
  showOperator = true,
  selectedCount = 0,
  isExplicitRange = false,
}: ActiveFilterChipProps) => {
  // Picking a 2nd value rewrites the operator label ("is" -> "is any of") as well as the value
  // label, so BOTH of the trigger's edges move — neither is a stable thing to freeze against.
  // The chip wrapper is, so the anchor lives there and holds the trigger's box within it.
  const anchor = useAnchorSnapshot<HTMLButtonElement, HTMLDivElement>(open);
  const { operatorFor, setOperator } = useFilters();
  const options = operatorOptions(field);
  const activeOperator = operatorFor(field);
  const operatorText =
    field.operatorLabel ??
    labelForOperator(activeOperator, field.type, {
      selectedCount,
      isExplicitRange,
    });

  return (
    <div
      ref={anchor.containerRef}
      className={cn(
        /* sorted */
        "relative",
        "flex",
        "gap-px items-center",
        "text-xs",
      )}
    >
      <ActiveFilterField label={field.label} icon={field.icon} />
      {showOperator && (
        <ActiveFilterOperator
          options={options}
          label={operatorText}
          onChange={(operator) => setOperator(field, operator)}
        />
      )}

      <Popover modal={true} open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger
          ref={anchor.triggerRef}
          className={cn(
            /* sorted */
            "shrink-0",
            "h-6",
            "px-1 py-0.5",
            "text-xs leading-4 font-normal text-text-dark-high",
            "bg-bg-dark-min rounded-none border-t border-b border-border-dark-min cursor-pointer",
            "transition",
            "hover:bg-bg-dark-low",
          )}
        >
          {valueLabel}
        </PopoverTrigger>
        {anchor.style && (
          <PopoverAnchor asChild>
            <span
              aria-hidden
              className="pointer-events-none"
              style={anchor.style}
            />
          </PopoverAnchor>
        )}
        <PopoverContent className="p-0 w-auto">{children}</PopoverContent>
      </Popover>

      <ActiveFilterRemove onClick={onRemove} />
    </div>
  );
};
